import { apiClient } from './client';
import type {
  ListRoomsResponse as ApiListRoomsResponse,
  RoomDetailResponse as ApiRoomDetailResponse,
  MessagesResponse as ApiMessagesResponse,
  SendMessageRequest,
  SendMessageResponse as ApiSendMessageResponse,
  CreateRoomRequest,
  CreateRoomResponse as ApiCreateRoomResponse,
} from '../types/api';
import type { ChatMessageEvent, ChatRoomDto, RoomMemberDto } from '../types/chat';
import type { RoomAttachmentsResponse } from '../lib/room-attachments';

/**
 * Chat API 클라이언트 — backend `apps/chat-server/src/modules/chat/*.routes.ts` 와 1:1.
 *
 * **Wire SSOT**: `packages/shared-types/openapi/openapi.yaml` (OpenAPI Stage 3 정합).
 *
 * 본 모듈은 generated `Api.*Response` 를 axios 응답으로 받고, frontend 의 좁힌 타입
 * (`ChatRoomDto = Required<Api.ChatRoom>` 등) 으로 cast 하여 반환. backend 정책 (모든 컬럼을 항상
 * 발화) 을 type-level 에서 강제 — 응답 객체에 누락된 필드는 yaml drift 또는 backend 버그.
 * drift-check + CI 가 봉합.
 *
 * P2-16 사용 범위: listMyRooms / getRoom / getMessages / sendMessage.
 *
 * Idempotency-Key: 메시지 send 시 클라이언트가 UUID v4 생성 → backend P2-04b body hash mismatch
 * 시 412 응답.
 */

export interface ListRoomsResponse {
  rooms: ChatRoomDto[];
  nextCursor: string | null;
}

export interface RoomDetailResponse {
  room: ChatRoomDto;
  members: RoomMemberDto[];
}

export interface MessagesResponse {
  messages: ChatMessageEvent[];
  nextCursor: string | null;
}

export interface SendMessageResponse {
  message: ChatMessageEvent;
  reused?: boolean;
}

export interface CreateRoomResponse {
  room: ChatRoomDto;
  /** direct 방이 이미 존재해 재사용된 경우 true (백엔드 dedup). */
  reused?: boolean;
}

export async function listMyRooms(params?: { cursor?: string; limit?: number }): Promise<ListRoomsResponse> {
  const { data } = await apiClient.get<ApiListRoomsResponse>('/chat/rooms', { params });
  return narrowListRooms(data);
}

export async function getRoom(roomId: string): Promise<RoomDetailResponse> {
  const { data } = await apiClient.get<ApiRoomDetailResponse>(`/chat/rooms/${roomId}`);
  return narrowRoomDetail(data);
}

/**
 * 방 프로필 수정 — 이름 · 설명 · 이미지. (chat-detail-panel)
 *
 * 보낸 키만 바뀐다(부분 수정). 서버 권한은 OWNER/ADMIN 이고 1:1 방은 400 을 준다.
 */
export interface RoomProfilePatch { name?: string; description?: string; avatarUrl?: string }
export interface RoomProfileResult {
  id: string; name: string | null; description: string | null; avatarUrl: string | null; type: string;
}
export async function updateRoomProfile(roomId: string, patch: RoomProfilePatch): Promise<RoomProfileResult> {
  const { data } = await apiClient.patch<{ room: RoomProfileResult }>(`/chat/rooms/${roomId}`, patch);
  return data.room;
}

/** 방 알림 끄기/켜기 — 서버는 boolean 하나만 다룬다(멘션만은 미지원). */
export async function setRoomMuted(roomId: string, muted: boolean): Promise<void> {
  await apiClient.put(`/chat/rooms/${roomId}/mute`, { muted });
}

/**
 * 방 메시지 조회.
 * - 초기 로드: `{ since, minCount, limit }` — since(ISO) 이후를 시간창으로 우선 로드, 창 안이 적으면
 *   minCount 만큼 최신 보장, limit 으로 상한 캡. nextCursor 가 그 이전(과거) 페이지 커서.
 * - 이전 로드: `{ cursor, limit }` — cursor(commit_seq) 이전 페이지.
 */
export async function getMessages(
  roomId: string,
  params?: { cursor?: string; limit?: number; since?: string; minCount?: number },
): Promise<MessagesResponse> {
  const { data } = await apiClient.get<ApiMessagesResponse>(`/chat/rooms/${roomId}/messages`, { params });
  return narrowMessages(data);
}

/**
 * 방 **전체 대화** 안에서 본문 검색 (chat-message-search — 2026-09-17).
 *
 * 화면의 「대화 검색」은 종전에 **이미 불러온 메시지**만 훑었다. 이 창구는 방 전체를 서버가 찾는다.
 * 응답은 메시지 목록과 **같은 모양**이라 결과를 그대로 메시지처럼 다룰 수 있다(클릭 → 그 자리로 이동).
 *
 * ★ 여기까지가 «재현율» 이다 — 서버는 본문 날것을 ILIKE 로 맞히므로 마크다운·html 마크업도 걸린다.
 *   보여 줄 것을 고르는 «정밀도» 는 화면이 `chatPlainPreview` 로 한다(그 코드가 이미 있다).
 *
 * ⚠ 옛 서버는 이 라우트가 없어 **404** 다 — 호출부가 종전의 「불러온 것만 훑기」로 되돌아간다.
 */
export async function searchRoomMessages(
  roomId: string,
  params: { q: string; cursor?: string; limit?: number },
): Promise<MessagesResponse> {
  const { data } = await apiClient.get<ApiMessagesResponse>(
    `/chat/rooms/${roomId}/messages/search`,
    { params },
  );
  return narrowMessages(data);
}

/**
 * 방 **전체 대화** 의 첨부 조회 (chat-room-wide-attachments — 2026-07-10).
 *
 * 우측 미디어 패널은 원래 로드된 페이지네이션 창의 첨부만 보여줬다. 이 엔드포인트는 방 전체를
 * 대상으로 하며, 가시성(멤버십·삭제·AI 나만 보기)은 메시지 목록과 동일하게 서버가 강제한다.
 *
 * `limit` 은 **메시지** 수 상한이다 — 한 메시지에 첨부가 여럿일 수 있어 items 수는 그보다 클 수 있다.
 */
export async function getRoomAttachments(
  roomId: string,
  params?: { cursor?: string; limit?: number },
): Promise<RoomAttachmentsResponse> {
  const { data } = await apiClient.get<RoomAttachmentsResponse>(`/chat/rooms/${roomId}/attachments`, { params });
  return data;
}

/** 방 열람 읽음 처리 — lastReadId 전진 → unreadCount 0 (NavBar 채팅 배지 클리어). */
export async function markRoomRead(roomId: string): Promise<void> {
  await apiClient.post(`/chat/rooms/${roomId}/read`);
}

/**
 * 방 읽음 상태 조회 (chat-read-receipts) — 활성 멤버별 lastReadId 맵 + 진입 시점의 내 lastReadId.
 *
 * 클라이언트는 이 맵으로 메시지별 "아직 안 읽은 인원" 을 계산한다(1:1 이면 0/1, 그룹이면 N).
 * 이후 갱신은 SSE `chat_read` 증분 — 재조회는 방 재진입/재연결 때만.
 */
export interface RoomReadStatesResponse {
  roomId: string;
  states: Array<{ userId: string; lastReadId: string | null }>;
  myLastReadId: string | null;
}

export async function getRoomReadStates(roomId: string): Promise<RoomReadStatesResponse> {
  const { data } = await apiClient.get<RoomReadStatesResponse>(`/chat/rooms/${roomId}/read-states`);
  return {
    roomId: data?.roomId ?? roomId,
    states: Array.isArray(data?.states) ? data.states : [],
    myLastReadId: data?.myLastReadId ?? null,
  };
}

/** 방 나가기 — 본인 멤버십 leftAt=NOW. 멱등. */
export async function leaveRoom(roomId: string): Promise<{ roomId: string; left: boolean }> {
  const { data } = await apiClient.post<{ roomId: string; left: boolean }>(
    `/chat/rooms/${roomId}/leave`,
    {},
  );
  return data;
}

/**
 * 방에 멤버 추가(초대) — 방장/관리자만. direct(1:1) 방은 불가(400). 멱등(이미 멤버는 skip).
 * 반환: added(신규 추가된 userId)·members(갱신된 전체 멤버, GET /:id 와 동일 shape). (chat-room-invite)
 */
export async function addRoomMembers(
  roomId: string,
  userIds: string[],
): Promise<{ added: string[]; members: RoomMemberDto[] }> {
  const { data } = await apiClient.post<{ added: string[]; members: RoomMemberDto[] }>(
    `/chat/rooms/${roomId}/members`,
    { userIds },
  );
  return { added: data.added ?? [], members: Array.isArray(data.members) ? data.members : [] };
}

/**
 * 방장이 멤버를 내보낸다 — `DELETE /chat/rooms/:id/members/:userId` (서버 2026-09-16).
 * 서버 권한은 **OWNER 만**(초대보다 좁다). 1:1 400 · 자기 자신 400 · 방장 403.
 * 소프트 탈퇴(leftAt)라 다시 초대하면 되살아난다. 이미 나간 사람이면 `removed: false`(멱등).
 */
export async function removeRoomMember(
  roomId: string,
  userId: string,
): Promise<{ roomId: string; userId: string; removed: boolean }> {
  const { data } = await apiClient.delete<{ roomId: string; userId: string; removed: boolean }>(
    `/chat/rooms/${roomId}/members/${userId}`,
  );
  return data;
}

export async function sendMessage(
  roomId: string,
  body: SendMessageRequest,
  idempotencyKey: string,
): Promise<SendMessageResponse> {
  const { data } = await apiClient.post<ApiSendMessageResponse>(
    `/chat/rooms/${roomId}/messages`,
    body,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return narrowSendMessage(data);
}

/**
 * 방 생성 — direct(2명)/group/team. backend 가 direct 중복 시 기존 방 재사용(reused:true).
 * group/team 은 name 필수. memberIds 는 본인 제외 상대들(백엔드가 caller 자동 포함).
 */
export async function createRoom(body: CreateRoomRequest): Promise<CreateRoomResponse> {
  const { data } = await apiClient.post<ApiCreateRoomResponse>('/chat/rooms', body);
  return { room: data.room as ChatRoomDto, reused: data.reused };
}

/**
 * 메시지 삭제(전체 회수) — 본인이 보낸 메시지를 양쪽 화면에서 "삭제된 메시지입니다" 톰스톤으로.
 * 멱등. (chat-message-delete)
 */
export async function deleteMessage(
  roomId: string,
  messageId: string,
): Promise<{ roomId: string; messageId: string; deleted: boolean }> {
  const { data } = await apiClient.post<{ roomId: string; messageId: string; deleted: boolean }>(
    `/chat/rooms/${roomId}/messages/${messageId}/delete`,
    {},
  );
  return data;
}

/** 메시지 이모지 반응 토글 — 있으면 제거(removed), 없으면 추가(added). */
export async function toggleReaction(
  roomId: string,
  messageId: string,
  emoji: string,
): Promise<{ action: 'added' | 'removed'; emoji: string }> {
  const { data } = await apiClient.post<{ action: 'added' | 'removed'; emoji: string }>(
    `/chat/rooms/${roomId}/messages/${messageId}/reactions`,
    { emoji },
  );
  return data;
}

// === Wire → frontend 좁힘 cast ===
//
// generated `Api.*` 는 yaml 의 모든 필드를 optional + nullable 로 표현 (openapi-typescript 정책).
// backend 가 모든 컬럼을 항상 발화하므로 frontend 는 `Required<>` 좁힘 안전. 본 cast 가 단일 게이트
// — 응답에 실제 필드가 빠져 있으면 다음 cast (또는 Required 가드) 가 noop 이 아니라 사용처에서
// undefined 접근 즉시 발견.
//
// 향후 zod runtime validator 도입 시 본 narrow 함수가 schema.parse(data) 로 교체.

// v2.11.2 — 200 응답인데 목록 필드가 없는 이상 바디(레이트리밋/프록시 변형 등) 방어:
// undefined 통과 시 소비처(.length/.map)가 ErrorBoundary 로 죽는다 → 빈 배열로 강등.
function narrowListRooms(d: ApiListRoomsResponse): ListRoomsResponse {
  return {
    rooms: Array.isArray(d?.rooms) ? (d.rooms as ChatRoomDto[]) : [],
    nextCursor: d?.nextCursor ?? null,
  };
}

function narrowRoomDetail(d: ApiRoomDetailResponse): RoomDetailResponse {
  return {
    room: d.room as ChatRoomDto,
    members: Array.isArray(d?.members) ? (d.members as RoomMemberDto[]) : [],
  };
}

function narrowMessages(d: ApiMessagesResponse): MessagesResponse {
  return {
    messages: Array.isArray(d?.messages) ? (d.messages as ChatMessageEvent[]) : [],
    nextCursor: d?.nextCursor ?? null,
  };
}

function narrowSendMessage(d: ApiSendMessageResponse): SendMessageResponse {
  return { message: d.message as ChatMessageEvent, reused: d.reused };
}
